import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Database, RefreshCw } from "lucide-react";
import { toast } from "react-toastify";
import { adminApi } from "../../../api/adminApi";
import "./EmailSettings.css";

const EMPTY_FORM = {
  server: "",
  instanceName: "",
  trustServerCertificate: false,
  port: "1433",
  database: "TFOnlineDB",
  user: "",
  password: "",
};

const StoreDatabaseSettings = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [savedSnapshot, setSavedSnapshot] = useState(
    JSON.stringify(EMPTY_FORM),
  );
  const [connection, setConnection] = useState({
    configured: false,
    passwordConfigured: false,
    passwordFromEnvironment: false,
    lastTestedAt: null,
  });
  const [connectionTest, setConnectionTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    const loadSettings = async () => {
      try {
        const response = await adminApi.getStoreDatabaseSettings();
        if (!active) return;
        const data = response.data || {};
        const next = {
          ...EMPTY_FORM,
          server: data.server || "",
          instanceName: data.instanceName || "",
          trustServerCertificate: data.trustServerCertificate === true,
          port: String(data.port || 1433),
          database: data.database || "TFOnlineDB",
          user: data.user || "",
        };
        setForm(next);
        setSavedSnapshot(JSON.stringify(next));
        setConnection(data);
      } catch (loadError) {
        if (active) {
          setError(
            loadError.response?.data?.error ||
              "Could not load store database settings.",
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    loadSettings();
    return () => {
      active = false;
    };
  }, []);

  const isDirty = useMemo(
    () => JSON.stringify(form) !== savedSnapshot,
    [form, savedSnapshot],
  );
  const isSameSavedConnection =
    connection.configured &&
    form.server.trim() === connection.server &&
    form.instanceName.trim() === (connection.instanceName || "") &&
    Number(form.port) === connection.port &&
    form.database.trim() === connection.database &&
    form.user.trim() === connection.user;

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setConnectionTest(null);
  };

  const toPayload = () => ({
    ...form,
    port: Number(form.port),
  });

  const testConnection = async () => {
    setTesting(true);
    setError("");
    setConnectionTest(null);
    try {
      const response = await adminApi.testStoreDatabaseConnection(toPayload());
      setConnectionTest(response.data);
      toast.success(response.message || "Store database connection verified.");
    } catch (testError) {
      const message =
        testError.response?.data?.error || "Store database connection failed.";
      setError(message);
      toast.error(message);
    } finally {
      setTesting(false);
    }
  };

  const saveSettings = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const response = await adminApi.saveStoreDatabaseSettings(toPayload());
      const data = response.data || {};
      const next = {
        ...EMPTY_FORM,
        server: data.server || form.server,
        instanceName: data.instanceName || "",
        trustServerCertificate: data.trustServerCertificate === true,
        port: String(data.port || form.port),
        database: data.database || form.database,
        user: data.user || form.user,
      };
      setForm(next);
      setSavedSnapshot(JSON.stringify(next));
      setConnection(data);
      setConnectionTest(data.testResult || null);
      toast.success(response.message || "Store database connection saved.");
    } catch (saveError) {
      const message =
        saveError.response?.data?.error ||
        "Could not save store database settings.";
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="email-settings-page" aria-busy="true">
        <div className="es-skeleton es-sk-title" />
        <div className="es-skeleton es-sk-card" />
        <span className="es-sr-only">Loading store database settings</span>
      </div>
    );
  }

  return (
    <div className="email-settings-page">
      <header className="es-header">
        <h1>Store database connection</h1>
        <p>
          Connect to the online store database used to look up and assign stores
          to onboarding clients.
        </p>
      </header>

      {error && (
        <div className="es-alert es-alert-error" role="alert">
          <AlertTriangle size={18} aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      <form className="es-form" onSubmit={saveSettings}>
        <section className="es-card">
          <div className="es-card-head">
            <div className="es-card-head-row">
              <div>
                <h2>SQL Server</h2>
                <p>
                  Connection credentials are encrypted before they are stored.
                  The password is never returned to the browser.
                </p>
              </div>
              {connection.configured && (
                <span className="es-badge">Connection configured</span>
              )}
            </div>
          </div>

          <div className="es-group">
            <div className="es-grid">
              <div className="es-field">
                <label htmlFor="store-db-server">Server hostname or IP</label>
                <input
                  id="store-db-server"
                  className="es-input"
                  autoComplete="off"
                  value={form.server}
                  onChange={(event) =>
                    updateField("server", event.target.value)
                  }
                  placeholder="sql.example.internal"
                  required
                />
              </div>
              <div className="es-field">
                <label htmlFor="store-db-port">SQL Server port</label>
                <input
                  id="store-db-port"
                  className="es-input"
                  type="number"
                  min="1"
                  max="65535"
                  value={form.port}
                  onChange={(event) => updateField("port", event.target.value)}
                  required
                />
              </div>
              <div className="es-field">
                <label htmlFor="store-db-instance">
                  Database instance <span className="es-hint">(optional)</span>
                </label>
                <input
                  id="store-db-instance"
                  className="es-input"
                  autoComplete="off"
                  value={form.instanceName}
                  onChange={(event) =>
                    updateField("instanceName", event.target.value)
                  }
                  placeholder="SQLEXPRESS"
                />
              </div>
              <div className="es-field">
                <label htmlFor="store-db-name">Database name</label>
                <input
                  id="store-db-name"
                  className="es-input"
                  autoComplete="off"
                  value={form.database}
                  onChange={(event) =>
                    updateField("database", event.target.value)
                  }
                  required
                />
              </div>
              <div className="es-field">
                <label htmlFor="store-db-user">SQL username</label>
                <input
                  id="store-db-user"
                  className="es-input"
                  autoComplete="username"
                  value={form.user}
                  onChange={(event) => updateField("user", event.target.value)}
                  required
                />
              </div>
              <div className="es-field">
                <label htmlFor="store-db-password">
                  SQL Server password{" "}
                  {connection.passwordConfigured && (
                    <span className="es-badge">
                      {connection.passwordFromEnvironment
                        ? "Backend environment"
                        : "Saved"}
                    </span>
                  )}
                </label>
                <input
                  id="store-db-password"
                  className="es-input"
                  type="password"
                  autoComplete="new-password"
                  value={form.password}
                  onChange={(event) =>
                    updateField("password", event.target.value)
                  }
                  disabled={connection.passwordFromEnvironment}
                  placeholder={
                    connection.passwordFromEnvironment
                      ? "Using STORE_DB_PASSWORD from backend environment"
                      : isSameSavedConnection && connection.passwordConfigured
                      ? "Leave blank to keep the saved password"
                      : "Enter password for this connection"
                  }
                  required={
                    !connection.passwordFromEnvironment &&
                    (!isSameSavedConnection || !connection.passwordConfigured)
                  }
                />
              </div>
            </div>
            {connection.passwordFromEnvironment && (
              <p className="es-hint">
                The store database password is provided by the backend{" "}
                <code>STORE_DB_PASSWORD</code> setting. It is not shown in this
                page.
              </p>
            )}
            <label className="es-switch" htmlFor="store-db-trust-certificate">
              <input
                id="store-db-trust-certificate"
                type="checkbox"
                role="switch"
                checked={form.trustServerCertificate}
                onChange={(event) =>
                  updateField(
                    "trustServerCertificate",
                    event.target.checked,
                  )
                }
                aria-describedby="store-db-trust-certificate-desc"
              />
              <span className="es-switch-track" aria-hidden="true" />
              <span className="es-switch-text">
                <span className="es-switch-label">
                  Trust the store server certificate
                </span>
                <span
                  id="store-db-trust-certificate-desc"
                  className="es-switch-desc is-warn"
                >
                  Enable only for a trusted private network or when the SQL
                  Server uses a self-signed certificate. This skips server
                  certificate identity validation.
                </span>
              </span>
            </label>
            <p className="es-hint">
              Use a dedicated SQL login with SELECT access to{" "}
              <code>platform.Tenants</code> and EXECUTE access to{" "}
              <code>platform.CreateOnboardingTenant</code> for tenant
              provisioning. The login does not need direct INSERT rights.
              Credentials are configurable here, so update them if the login
              changes. TLS encryption is always enabled.
              Leave certificate trust disabled when using a CA-trusted server
              certificate. Leave the instance blank for the default SQL Server
              instance. When an instance is provided, SQL Server Browser
              resolves its port and the port field is ignored. No product,
              order, user, or session records are read by this store-assignment
              feature.
            </p>
          </div>

          <div className="es-card-footer">
            <button
              type="button"
              className="es-btn"
              onClick={testConnection}
              disabled={testing || saving}
            >
              {testing ? (
                <span className="es-spinner" aria-hidden="true" />
              ) : (
                <Database size={16} aria-hidden="true" />
              )}
              {testing ? "Testing…" : "Test connection"}
            </button>
            <p className="es-hint">
              The test verifies tenant provisioning and user-copy permissions
              without changing store data.
            </p>
          </div>
        </section>

        {(connectionTest || connection.lastTestedAt) && (
          <div className="es-alert" role="status">
            <CheckCircle2 size={18} aria-hidden="true" />
            <span>
              {connectionTest
                ? `Connected to ${connectionTest.database}; ${connectionTest.tenantCount} tenant(s) found; tenant provisioning and user-copy permissions verified.`
                : `Last successful test: ${new Date(
                    connection.lastTestedAt,
                  ).toLocaleString()}`}
            </span>
          </div>
        )}

        <div className="es-savebar" role="region" aria-label="Save settings">
          <span className="es-savebar-status">
            {isDirty ? "Unsaved connection changes" : "Connection settings saved"}
          </span>
          <button
            type="submit"
            className="es-btn es-btn-primary"
            disabled={!isDirty || saving || testing}
          >
            {saving ? (
              <>
                <span className="es-spinner" aria-hidden="true" />
                Saving…
              </>
            ) : (
              <>
                <RefreshCw size={15} aria-hidden="true" />
                Test and save connection
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default StoreDatabaseSettings;
